import { apiClient, ApiError } from './apiClient'
import { generalSettingsService } from './generalSettingsService'

const STATUT_LABELS = {
  actif: 'Actif',
  inactif: 'Inactif'
}

const STATUT_BADGES = {
  actif: 'bg-success',
  inactif: 'bg-danger'
}

const FORMES_JURIDIQUES = [
  { value: 'SARL', label: 'SARL' },
  { value: 'SA', label: 'SA' },
  { value: 'SUARL', label: 'SUARL' },
  { value: 'EI', label: 'Entreprise individuelle' },
  { value: 'GIE', label: 'GIE' }
]

let cachedCompanies = []

const normalizeCompany = (company) => ({
  ...company,
  id: company.id,
  userCount: company.userCount ?? 0,
  protectedCompany: company.protectedCompany ?? company.siege ?? false
})

const buildPayload = (data) => ({
  raisonSociale: data.raisonSociale?.trim(),
  sigle: data.sigle?.trim().toUpperCase(),
  formeJuridique: data.formeJuridique || 'SARL',
  ncc: data.ncc?.trim() || '',
  rccm: data.rccm?.trim() || '',
  adresse: data.adresse?.trim() || '',
  commune: data.commune?.trim() || '',
  telephone: data.telephone?.trim() || '',
  email: data.email?.trim().toLowerCase(),
  siteWeb: data.siteWeb?.trim() || '',
  responsable: data.responsable?.trim() || '',
  statut: data.statut || 'actif'
})

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email?.trim())

export const companiesService = {
  async getAll(params = {}) {
    const data = await apiClient.get('/companies', params)
    if (!Array.isArray(data)) {
      throw new ApiError('Réponse invalide du serveur (liste entreprises attendue).', 500)
    }
    cachedCompanies = data.map(normalizeCompany)
    return cachedCompanies
  },

  async getSummary() {
    return apiClient.get('/companies/summary')
  },

  async getActive() {
    const data = await apiClient.get('/companies/active')
    return data.map(normalizeCompany)
  },

  getCachedAll() {
    return [...cachedCompanies]
  },

  getById(id) {
    return cachedCompanies.find((c) => String(c.id) === String(id)) || null
  },

  getLabel(id) {
    const company = this.getById(id)
    return company?.raisonSociale || '—'
  },

  async resolveDefaultCompanyName() {
    let settings = generalSettingsService.getCached()
    if (!settings) {
      try {
        settings = await generalSettingsService.fetchAll()
      } catch {
        settings = null
      }
    }

    let companies = this.getCachedAll()
    if (!companies.length) {
      try {
        companies = await this.getAll()
      } catch {
        companies = []
      }
    }

    if (settings?.defaultCompanyId) {
      const defaultCompany = companies.find(
        (company) => String(company.id) === String(settings.defaultCompanyId)
      )
      if (defaultCompany?.raisonSociale) {
        return defaultCompany.raisonSociale
      }
    }

    const siegeCompany = companies.find((company) => company.siege || company.protectedCompany)
    if (siegeCompany?.raisonSociale) {
      return siegeCompany.raisonSociale
    }

    return companies[0]?.raisonSociale || ''
  },

  isProtected(companyOrId) {
    if (typeof companyOrId === 'object' && companyOrId !== null) {
      return !!(companyOrId.protectedCompany || companyOrId.siege)
    }
    const company = cachedCompanies.find((c) => String(c.id) === String(companyOrId))
    return !!(company?.protectedCompany || company?.siege)
  },

  sigleExists(sigle, excludeId = null) {
    const normalized = sigle?.trim().toUpperCase()
    if (!normalized) return false
    return cachedCompanies.some((c) => c.sigle?.toUpperCase() === normalized && String(c.id) !== String(excludeId))
  },

  async create(data) {
    const created = await apiClient.post('/companies', buildPayload(data))
    return normalizeCompany(created)
  },

  async update(id, data) {
    const existing = this.getById(id)
    const payload = buildPayload({
      raisonSociale: data.raisonSociale ?? existing?.raisonSociale,
      sigle: data.sigle ?? existing?.sigle,
      formeJuridique: data.formeJuridique ?? existing?.formeJuridique,
      ncc: data.ncc ?? existing?.ncc,
      rccm: data.rccm ?? existing?.rccm,
      adresse: data.adresse ?? existing?.adresse,
      commune: data.commune ?? existing?.commune,
      telephone: data.telephone ?? existing?.telephone,
      email: data.email ?? existing?.email,
      siteWeb: data.siteWeb ?? existing?.siteWeb,
      responsable: data.responsable ?? existing?.responsable,
      statut: data.statut ?? existing?.statut ?? 'actif'
    })
    const updated = await apiClient.put(`/companies/${id}`, payload)
    return normalizeCompany(updated)
  },

  async updateStatut(id, statut) {
    const updated = await apiClient.patch(`/companies/${id}/statut`, { statut })
    return normalizeCompany(updated)
  },

  async delete(id) {
    await apiClient.delete(`/companies/${id}`)
    return true
  },

  countByCompany(companyId) {
    return cachedCompanies.find((c) => String(c.id) === String(companyId))?.userCount ?? 0
  },

  getFormesJuridiques() {
    return FORMES_JURIDIQUES
  },

  isValidEmail,

  getStatutLabel(statut) {
    return STATUT_LABELS[statut] || statut
  },

  getStatutBadge(statut) {
    return STATUT_BADGES[statut] || 'bg-secondary'
  }
}
